import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils";
import { Group, Layout, Page, Tab, Tree } from "@/comn/components";
import { useFetch, useForm, useModal, useToast, useTab, useResource, useTree, usePage, useStore } from "@/comn/hooks";
import { APIS, SF_ORG_MGMT_INFO, SF_ORG_DEPT_INFO, URLS } from "@/tz/go/egaz/osci/ptl/usr/org/services/OsciOrgService";

/*
 * Modifying and Deleting Organization Management
 * !== 기관 관리 수정 및 삭제 ==!
 */
export const OSCI0103007S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const tree = useTree(); // Tree Hook !== Tree Hook ==!
    const { orgCd } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!

    const { updateResource } = useResource({
        // 수정 성공 후 갱신하는 기관 코드 리소스를 화면 수명주기에도 명시적으로 등록한다.
        defaultSchema: [{ area: "orgDeptCd" }, { area: "orgCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organization Information Form !== 기관 정보 form ==!
        orgInfo: useForm({
            defaultSchema: SF_ORG_MGMT_INFO,
            defaultValues: {},
        }),
        // Organization Information Form !== 기관의 부서 정보 form ==!
        deptInfo: useForm({
            defaultSchema: SF_ORG_DEPT_INFO,
            defaultValues: {},
        }),
    };

    // Organization, Department Tab !== 기관,부서 탭 ==!
    const tab = useTab({
        defaultSchema: {
            id: "tab",
            schema: [{ label: "L_ORG" }, { label: "L_ORG_DEPT" }],
        },
    });

    // TIN 값 중복 확인 전, 기존 TIN에서 변경이 됬는지 확인을 위해 변수 정의
    const prevTin = useRef<string>("");

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Check Organization Information !== 기관 정보 조회 ==!
        getOrgInfo: useFetch({
            api: (data) => APIS.getOrgInfo(orgCd),
            enabled: !!orgCd,
            onSuccess: (data) => {
                const orgInfo = data.orgInfo.content;
                form.orgInfo.setValues(orgInfo);
                prevTin.current = form.orgInfo.getValue("tin") ?? "";
                // 지역구분은 수정 불가.
                // 기존 MAIN LAND 기관도 값이 표시되어야 하므로 두 선택지를 모두 유지하고,
                // 조회 모드(edit:false)에서는 옵션 label 이 번역되지 않으므로 t() 로 변환한 문구를 넣는다.
                form.orgInfo.setSchema("orgRegnDvnCd", {
                    edit: false,
                    viewType: "label",
                    options: [
                        { label: t("L_REGN_M_CD"), value: "M" },
                        { label: t("L_REGN_Z_CD"), value: "Z" },
                    ],
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00035" }), // An error occurred. !== 에러가 발생했습니다! ==!
        }),

        // Check Organization Department Information !== 기관의 부서 정보 조회 ==!
        getDeptInfo: useFetch({
            api: (data) => APIS.getOrgDeptInfo(orgCd, data.deptCd),
            onSuccess: (data) => {
                const deptInfo = data.deptInfo.content;
                form.deptInfo.setValues(deptInfo);
            },
            onError: (error) => {},
            showToast: true,
        }),
        // Update Organization Information !== 기관 정보 수정 ==!
        updateOrgInfo: useFetch({
            api: (info: any) => APIS.updateOrgInfo(info),
            enabled: false,
            onSuccess: async () => {
                // 수정된 기관 정보가 Organization Code 자동완성에 즉시 반영되도록 공통 리소스를 갱신한다.
                await updateResource("orgCd");
                modal.openModal({ content: "msg.osc.00070" }); // Successfully updated. !== 정상적으로 수정되었습니다.==!
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00039" }), // Failed to update. !== 수정이 실패하였습니다.==!
        }),
        // Save Organization Department Information !== 기관의 부서 정보 저장 ==!
        saveOrgDeptInfo: useFetch({
            api: (info: any) => APIS.saveOrgDeptInfo(info),
            enabled: false,
            onSuccess: () => {
                modal.openModal({ content: "msg.osc.00070" }); // Successfully updated. !== 정상적으로 수정되었습니다.==!
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00039" }), // Failed to update. !== 수정이 실패하였습니다.==!
        }),
        // Check Department List of the Organization !== 기관의 부서 목록 조회 ==!
        getDeptList: useFetch({
            api: () => APIS.getOrgDeptList(orgCd),
            onSuccess: (data) => {
                const deptList = data["deptList"].content;
                tree.setData([
                    {
                        name: "ROOT",
                        deptPath: "ROOT",
                        children: handler.createTreeData(deptList, [], 1, ""),
                    },
                ]);

                tree.openTree();
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00043" }), // 조회가 실패하였습니다.
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        initDeptForm: () => {
            form.deptInfo.setValue("deptPath", "ROOT");
        },
        // Click List Button !== 목록 버튼 클릭 ==!
        onListBtnClick: () => navigate(URLS.osci0103005q),

        // onUpdateBtnClick: form.orgInfo.handleSubmit(
        //     () => {
        //         modal.openModal({
        //             content: "msg.osc.00064", // Confirm update? !== 수정하시겠습니까? ==!
        //             onConfirm: () => fetch.updateOrgInfo.fetch(form.orgInfo.getValues()),
        //         });
        //     },
        //     () => toast.showToast({ type: "warning", content: "msg.osc.00003" }), // Data is invalid, !== 데이터가 유효하지않습니다. ==!
        // ),

        // Click Update Button !== 수정 버튼 클릭 ==!
        onUpdateBtnClick: async () => {
            const regex = /^\d{9}$/; // TIN을 체크하는 정규식 1. 숫자로만 이루어짐, 2. 9자리수로 이루어져야함
            const data = form.orgInfo.getValues();
            // 정규화를 통한 TIN 조건 처리
            if (!regex.test(form.orgInfo.getValue("tin"))) {
                modal.openModal({
                    content: "msg.osc.00076", // TIN must be 9 digits. ? !== TIN은 9자리여야 합니다. ? ==!
                });
                return;
            }

            // TIN 중복 여부 처리
            // 이전 TIN과 비교하여 변경된 사실이 있을 때, 중복을 검사하도록 설정
            if (prevTin.current !== form.orgInfo.getValue("tin")) {
                const resp = await APIS.getOrgTinDuplCheck(form.orgInfo.getValue("tin"));
                const isDup = resp.data.orgTinDuplCheck;
                if (isDup.content) {
                    modal.openModal({ content: "msg.osc.00232" }); // This TIN number is already in use. ? !== 이미 사용 중인 TIN 번호입니다. ? ==!
                    return;
                }
            }

            form.orgInfo.handleSubmit(
                () => {
                    modal.openModal({
                        content: "msg.osc.00064", // Confirm update? !== 수정하시겠습니까? ==!
                        onConfirm: () => {
                            fetch.updateOrgInfo.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },

        onUpdateOrgDeptBtnClick: form.deptInfo.handleSubmit(
            () => {
                form.deptInfo.setValue("orgCd", orgCd);
                modal.openModal({
                    content: "msg.osc.00064", // Confirm update? !== 수정하시겠습니까? ==!
                    onConfirm: () => fetch.saveOrgDeptInfo.fetch(form.deptInfo.getValues()),
                });
            },
            () => toast.showToast({ type: "warning", content: "msg.osc.00003" }), // Data is invalid, !== 데이터가 유효하지않습니다. ==!
        ),
        // Click Department Tree !== 기관의 부서 트리 클릭 ==!
        onTreeClick: (treeInfo: any) => {
            handler.initDeptForm();
            fetch.getDeptInfo.fetch(treeInfo);
        },

        // create Department Tree Data  !== 기관의 부서 Tree 데이터 생성 ==!
        createTreeData: (deptList: any, newList: any, level: any, deptCd: any) => {
            const tempList = deptList
                .map((item: any) => {
                    return {
                        name: comnUtils.replaceEmpty(item.deptNm),
                        checkbox: false,
                        deptCd: comnUtils.replaceEmpty(item.deptCd),
                        deptNm: comnUtils.replaceEmpty(item.deptNm),
                        deptLvl: comnUtils.replaceEmpty(item.deptLvl, 0),
                        deptIdtnOrd: comnUtils.replaceEmpty(item.deptIdtnOrd, 0),
                        uprDeptCd: comnUtils.replaceEmpty(item.uprDeptCd),
                        orgCd: comnUtils.replaceEmpty(item.orgCd),
                    };
                })
                .filter((item: any) => item["deptLvl"] === level && item["uprDeptCd"] === deptCd);

            if (tempList.length === 0) return newList;

            tempList.forEach((item: any) => {
                if (!item.children) item.children = [];
                handler.createTreeData(deptList, item.children, item["deptLvl"] + 1, item["deptCd"]);
                newList.push(item);
            });
            return newList;
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getOrgInfo.fetch();
        form.deptInfo.setEditable(false);
        handler.initDeptForm();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group>
                        <Group.Body>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.orgCd}></Group.Control>
                                    <Group.Control {...form.orgInfo.schema.orgRegnDvnCd}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.orgNm}></Group.Control>
                                    <Group.Control {...form.orgInfo.schema.orgAbrtNm}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.tin}></Group.Control>
                                    <Group.Control {...form.orgInfo.schema.hgovNm}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.rprsTelno}></Group.Control>
                                    <Group.Control {...form.orgInfo.schema.rprsFaxNo}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.lpcoIssuYn}></Group.Control>
                                    <Group.Control {...form.orgInfo.schema.orgStatCd}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.postAddr}></Group.Control>
                                </Group.Row>
                            </Group.Section>
                            <Layout direction="row">
                                <Layout.Left>
                                    <Layout.Button role={"list"} onClick={handler.onListBtnClick} />
                                </Layout.Left>
                                <Layout.Right>
                                    <Layout.Button role={"save"} onClick={handler.onUpdateBtnClick} />
                                </Layout.Right>
                            </Layout>
                        </Group.Body>
                    </Group>
                </Group.Body>
            </Group>
        </Page>
    );
};
